'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ControlledInput, Typography } from 'components';
import { DateValueType } from 'components/Inputs/DateRange/Datepicker';
import { ThemeStatus } from 'constants/status';
import { QualityEnum, TranslationRequestInput } from 'gql/graphql';
import { handlerEnter } from 'handlers/handlerEnter';
import { toaster } from 'helpers/toaster';
import useOptionsOrderableTargetLanguages from 'hooks/useOptionsOrderableTargetLanguages';
import useOptionsQuality from 'hooks/useOptionsQuality';
import useOptionsSourceLanguage from 'hooks/useOptionsSourceLanguage';
import useOptionsStyleGuides from 'hooks/useOptionsStyleGuides';
import { ArrowSelectIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { useTranslationRequestReplaceQuote } from 'providers/TranslationRequests/useTranslationRequestReplaceQuote';
import { FC, HTMLAttributes, memo, PropsWithChildren, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import { TScheme } from 'types/TScheme';
import { cn } from 'utils/cn';
import * as yup from 'yup';

interface IEditQuoteCardProps
  extends Omit<
    PropsWithChildren<HTMLAttributes<HTMLDivElement>>,
    'onSelect' | 'value' | 'onChange'
  > {
  defaultParams: Partial<TranslationRequestInput>;
  status: string;
  themeStatus: ThemeStatus;
  isPending: boolean;
  version: string;
  onCancel: () => void;
  refetch: () => void;
}

interface SchemaType
  extends Omit<Partial<TranslationRequestInput>, 'proposedClientDeadline' | 'targetLanguageCodes'> {
  proposedClientDeadline: DateValueType;
  targetLanguageCodes: string;
}

const EditQuoteCard: FC<IEditQuoteCardProps> = ({
  defaultParams,
  version,
  themeStatus,
  status,
  isPending,
  onCancel,
  refetch
}) => {
  const t = useTranslations();

  const { optionsQuality } = useOptionsQuality({ noAny: true });
  const { optionsSourceLanguages } = useOptionsSourceLanguage();
  const { optionsOrderableTargetLanguages: optionsTargetLanguages } =
    useOptionsOrderableTargetLanguages();

  const { optionsStyleGuides } = useOptionsStyleGuides();

  const { mutateAsync: translationRequestReplaceQuote, isPending: isLoadingMutating } =
    useTranslationRequestReplaceQuote();

  const schema = yup.object().shape({} as TScheme<SchemaType>);
  const { control, setValue, getValues, handleSubmit } = useForm<SchemaType>({
    resolver: yupResolver(schema)
  });

  const onUpdate = useCallback(async () => {
    try {
      const values = getValues();
      const response = await translationRequestReplaceQuote({
        translationRequestParams: {
          title: values.title,
          purchaseOrderCode: values.purchaseOrderCode,
          proposedClientDeadline: new Date(
            values.proposedClientDeadline?.endDate as string
          ).toISOString(),
          quality: values.quality as QualityEnum,
          sourceLanguageCode: values.sourceLanguageCode as string,
          targetLanguageCodes: [values.targetLanguageCodes] as string[],
          styleGuideReferenceId: values.styleGuideReferenceId,
          keyValueData: values.keyValueData
        },
        translationRequestVersion: version
      });

      if (response.success) {
        refetch();
        onCancel();
        toaster.success(t('messages.success.quote_edit'));
      }
    } catch (error) {
      notifyException(error);
      toaster.error(t('messages.error.quote_edit'));
    }
  }, [getValues, onCancel, refetch, t, translationRequestReplaceQuote, version]);

  const onSubmit = handleSubmit(onUpdate);

  useEffect(() => {
    setValue('title', defaultParams.title);
    setValue('purchaseOrderCode', defaultParams.purchaseOrderCode);
    setValue('proposedClientDeadline', {
      startDate: defaultParams.proposedClientDeadline as string,
      endDate: defaultParams.proposedClientDeadline as string
    });
    setValue('quality', defaultParams.quality);
    setValue('sourceLanguageCode', defaultParams.sourceLanguageCode);
    setValue('targetLanguageCodes', defaultParams.targetLanguageCodes?.at(0) ?? '');
    setValue('styleGuideReferenceId', defaultParams.styleGuideReferenceId);
  }, [
    defaultParams.proposedClientDeadline,
    defaultParams.purchaseOrderCode,
    defaultParams.quality,
    defaultParams.sourceLanguageCode,
    defaultParams.styleGuideReferenceId,
    defaultParams.targetLanguageCodes,
    defaultParams.title,
    setValue
  ]);

  return (
    <div
      className={cn(
        'w-full rounded-[5px] border-1 border-transparent bg-white p-4 shadow-custom ',
        'transition duration-200 ease-in-out'
      )}
    >
      <div className="flex flex-row justify-between">
        <div className="flex flex-row gap-2">
          {themeStatus?.icon}
          <Typography variant="regular4" className="text-tertiary-100 ">
            {status}
          </Typography>
        </div>
      </div>
      <div className="mb-4 grid grid-cols-12 gap-x-4">
        <ControlledInput
          control={control}
          label={t('translation.tr.quote_edit.title.label')}
          placeholder={t('translation.tr.quote_edit.title.placeholder')}
          type="text"
          name="title"
          classNameParent="col-span-6"
        />
        <ControlledInput
          control={control}
          label={t('translation.tr.quote_edit.po_code.label')}
          placeholder={t('translation.tr.quote_edit.po_code.placeholder')}
          type="text"
          name="purchaseOrderCode"
          classNameParent="col-span-6"
        />
        <div className="col-span-6">
          <span className="flex items-start gap-1">
            <ControlledInput
              control={control}
              label={t('translation.tr.quote_edit.source_language.label')}
              placeholder={t('translation.tr.quote_edit.source_language.placeholder')}
              type="combobox"
              name="sourceLanguageCode"
              classNameParent="w-full"
              options={optionsSourceLanguages}
            />
            <ArrowSelectIcon className="mt-10 h-10 w-10 -rotate-90 text-gray-700" />
            <ControlledInput
              control={control}
              label={t('translation.tr.quote_edit.target_language.label')}
              placeholder={t('translation.tr.quote_edit.target_language.placeholder')}
              type="combobox"
              options={optionsTargetLanguages}
              name="targetLanguageCodes"
              classNameParent="w-full"
            />
          </span>
        </div>
        <ControlledInput
          control={control}
          label={t('translation.tr.quote_edit.quality.label')}
          placeholder={t('translation.tr.quote_edit.quality.placeholder')}
          type="select"
          name="quality"
          classNameParent="col-span-3"
          options={optionsQuality}
        />
        <ControlledInput
          control={control}
          label={t('translation.tr.quote_edit.style_guide.label')}
          placeholder={t('translation.tr.quote_edit.style_guide.placeholder')}
          type="select"
          name="styleGuideReferenceId"
          classNameParent="col-span-3"
          options={optionsStyleGuides}
        />
        <ControlledInput
          control={control}
          type="date-range"
          name="proposedClientDeadline"
          label={t('translation.filters_translation.label_preferred_date')}
          classNameParent="col-span-3"
          optionalParams={{ asSingle: true }}
        />
      </div>
      <div
        className={cn(
          'flex flex-row justify-between',
          !isPending ? 'border-t-1 border-tertiary-600 pt-4' : ''
        )}
      >
        {!isPending && (
          <div className="flex flex-1 justify-end">
            <Button
              size="medium"
              variant="text"
              label={t('translation.tr.quote_edit.cancel')}
              onClick={onCancel}
            />
            <Button
              size="medium"
              label={t('translation.tr.quote_edit.save_changes')}
              onClick={onSubmit}
              onKeyDown={handlerEnter(onSubmit)}
              loading={isLoadingMutating}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(EditQuoteCard);
